import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ListChecks } from "lucide-react";
import {
  STATE_BY_KEY, formatMoney, daysBadgeClass,
} from "./pipelineHelpers";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// Work Queue tab — dense, sortable triage table
// =====================================================================
// Same plan data as Pipeline tab, but flattened into rows. Default filter
// is "Action needed" = states where staff can recover revenue or prevent
// leakage today.
//
// Click any row → opens TxPlanDrawer via the parent's onOpen handler.
// =====================================================================

const ACTIONABLE_STATES = new Set([
  "proposed",
  "needs_booking",
  "paused",
  "ar",
  "expired",
  "abandoned",
]);

const COLUMNS = [
  { key: "patient",  label: "Patient",      width: "w-[180px]", sortable: true },
  { key: "summary",  label: "Plan",         width: "flex-1",    sortable: false },
  { key: "doctor",   label: "Doctor",       width: "w-[120px]", sortable: true },
  { key: "value",    label: "Value",        width: "w-[100px]", sortable: true,  align: "right" },
  { key: "state",    label: "Stage",        width: "w-[110px]", sortable: true },
  { key: "days",     label: "Days",         width: "w-[60px]",  sortable: true,  align: "right" },
  { key: "visits",   label: "Visits",       width: "w-[70px]",  sortable: false, align: "right" },
];

export default function WorkQueueTab({ plans, currency = "PHP", loading, error, onOpen }) {
  const [scope, setScope]     = useState("actionable"); // "actionable" | "all"
  const [sortKey, setSortKey] = useState("days");
  const [sortDir, setSortDir] = useState("desc");

  const rows = useMemo(() => {
    const filtered = (plans || []).filter((p) =>
      scope === "all" ? true : ACTIONABLE_STATES.has(p.pipeline_state)
    );
    const sorted = [...filtered].sort((a, b) => sortFn(a, b, sortKey, sortDir));
    return sorted;
  }, [plans, scope, sortKey, sortDir]);

  const onSort = (key) => {
    if (!COLUMNS.find((c) => c.key === key)?.sortable) return;
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "value" || key === "days" ? "desc" : "asc");
    }
  };

  return (
    <div data-testid="tx-plan-center-work-queue" className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-[12px]">
          {[
            { key: "actionable", label: "Action needed" },
            { key: "all",        label: "All plans" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              className={[
                "px-3 py-1 rounded",
                scope === key
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {loading ? "loading…" : `${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">
          <div className="font-semibold">Couldn’t load work queue.</div>
          <div className="mt-1 text-rose-700">{String(error.message || error)}</div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {COLUMNS.map((c) => {
                    const active = sortKey === c.key;
                    const ChevIcon = sortDir === "asc" ? ChevronUp : ChevronDown;
                    return (
                      <th
                        key={c.key}
                        onClick={() => onSort(c.key)}
                        className={[
                          "px-3 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wide select-none",
                          c.align === "right" ? "text-right" : "text-left",
                          c.sortable ? "cursor-pointer hover:text-slate-900" : "",
                          c.width,
                        ].join(" ")}
                      >
                        <span className="inline-flex items-center gap-1">
                          {c.label}
                          {active && c.sortable ? (
                            <ChevIcon className="w-3 h-3" strokeWidth={2.5} />
                          ) : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan={COLUMNS.length} className="px-3 py-2.5">
                        <div className="h-4 bg-slate-100 rounded" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-3 py-10 text-center">
                      <div className="inline-flex flex-col items-center gap-2 text-slate-500">
                        <ListChecks className="w-7 h-7 text-slate-300" strokeWidth={1.75} />
                        <div className="text-[13px] font-medium text-slate-700">
                          {scope === "actionable" ? "Inbox zero." : "No plans match these filters."}
                        </div>
                        <div className="text-[11px]">
                          {scope === "actionable"
                            ? "No recoverable or blocked plans need a touch."
                            : "Try resetting filters in the sidebar."}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((p) => (
                    <Row key={p.id} plan={p} currency={currency} onOpen={onOpen} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ plan, currency, onOpen }) {
  const state = STATE_BY_KEY[plan.pipeline_state] ?? STATE_BY_KEY.abandoned;
  const StateIcon = state.Icon;
  return (
    <tr
      onClick={() => onOpen?.(plan.id)}
      className="cursor-pointer hover:bg-slate-50"
      data-testid={`work-queue-row-${plan.id}`}
    >
      <td className="px-3 py-2 align-top">
        <div className="font-semibold text-slate-900 truncate">{plan.patient_name || "—"}</div>
        <div className="text-[10px] text-slate-500 font-mono truncate">
          {[plan.patient_number, plan.plan_code].filter(Boolean).join(" · ")}
        </div>
      </td>
      <td className="px-3 py-2 align-top text-slate-700">
        <div className="truncate" title={plan.plan_summary || ""}>
          {plan.plan_summary || "—"}
        </div>
        {plan.open_task_count > 0 ? (
          <div className="mt-0.5 text-[10px] text-amber-700 font-medium">
            {plan.open_task_count} open task{plan.open_task_count === 1 ? "" : "s"}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 align-top text-slate-700 truncate">
        {formatDoctorName(plan.doctor_name, "—")}
      </td>
      <td className="px-3 py-2 align-top text-right font-semibold tabular-nums text-slate-900">
        {formatMoney(plan.total_centavos, currency)}
      </td>
      <td className="px-3 py-2 align-top">
        <span
          className={[
            "px-1.5 py-0.5 rounded-md text-[10px] font-medium inline-flex items-center gap-1",
            state.pill,
          ].join(" ")}
        >
          <StateIcon className="w-3 h-3" strokeWidth={2.5} />
          {state.label}
        </span>
      </td>
      <td className="px-3 py-2 align-top text-right">
        <span
          className={[
            "px-1.5 py-0.5 rounded-md text-[10px] font-medium tabular-nums",
            daysBadgeClass(plan.days_in_state, plan.pipeline_state),
          ].join(" ")}
        >
          {plan.days_in_state ?? 0}d
        </span>
      </td>
      <td className="px-3 py-2 align-top text-right text-slate-500 tabular-nums">
        {plan.total_visits > 0 ? `${plan.completed_visits}/${plan.total_visits}` : "—"}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------
function sortFn(a, b, key, dir) {
  const mult = dir === "asc" ? 1 : -1;
  switch (key) {
    case "patient":
      return mult * (a.patient_name || "").localeCompare(b.patient_name || "");
    case "doctor":
      return mult * (a.doctor_name || "").localeCompare(b.doctor_name || "");
    case "value":
      return mult * ((a.total_centavos || 0) - (b.total_centavos || 0));
    case "days":
      return mult * ((a.days_in_state || 0) - (b.days_in_state || 0));
    case "state": {
      const order = [
        "draft",
        "proposed",
        "needs_booking",
        "scheduled",
        "in_progress",
        "paused",
        "completed",
        "ar",
        "expired",
        "abandoned",
        "rejected",
        "superseded",
      ];
      return mult * (order.indexOf(a.pipeline_state) - order.indexOf(b.pipeline_state));
    }
    default:
      return 0;
  }
}
